import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Cpu, Wifi, Camera, Zap, Activity, Smartphone, Microscope, Shield } from 'lucide-react';

const ResearchDevelopmentPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const features = [
    {
      title: 'Custom Embedded Systems',
      description: 'Specialized embedded system development for defense and intelligence applications. Our custom solutions include microcontroller programming, real-time operating systems, and hardware-software integration for mission-critical operations requiring extreme reliability and security.',
      icon: Cpu,
      image: 'https://images.unsplash.com/photo-1650530415027-dc9199f473ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'IoT Device Development',
      description: 'Advanced Internet of Things solutions with secure communication protocols, edge computing capabilities, and sensor integration. Our IoT systems provide covert monitoring, environmental sensing, and data collection for intelligence and security operations.',
      icon: Wifi,
      image: 'https://images.unsplash.com/photo-1595692682118-774e5182f484?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Specialized Spy Devices',
      description: 'Covert surveillance and intelligence gathering devices including miniaturized cameras, audio recording systems, and communication intercept equipment. Our devices are designed for discrete operation with advanced encryption and secure data transmission.',
      icon: Camera,
      image: 'https://images.unsplash.com/photo-1634452021892-eaabbdc4a0bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'High-Performance Circuit Design',
      description: 'Custom PCB design and development for specialized applications including RF circuits, power electronics, and high-speed digital systems. Our designs meet stringent military and aerospace standards for reliability, EMI/EMC compliance, and environmental resilience.',
      icon: Activity,
      image: 'https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg',
    },
    {
      title: 'Secure Communication Systems',
      description: 'Development of encrypted communication devices and secure data transmission systems. Our solutions include tactical radios, secure messaging platforms, and frequency-hopping spread spectrum systems for military and intelligence applications.',
      icon: Smartphone,
      image: 'https://images.unsplash.com/photo-1650530415027-dc9199f473ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Sensor Integration & Analytics',
      description: 'Multi-sensor fusion systems combining various sensing modalities for enhanced situational awareness. Our solutions integrate thermal, optical, acoustic, and chemical sensors with AI-powered analytics for threat detection and environmental monitoring.',
      icon: Zap,
      image: 'https://images.unsplash.com/photo-1595692682118-774e5182f484?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Advanced Prototyping Lab',
      description: 'State-of-the-art R&D laboratory with rapid prototyping capabilities, testing equipment, and validation systems. Our facility supports the complete product development lifecycle from concept to field-ready deployment for defense and intelligence applications.',
      icon: Microscope,
      image: 'https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg',
    },
    {
      title: 'Cybersecurity Hardware',
      description: 'Hardware security modules, trusted platform modules, and secure boot systems for protecting sensitive electronics. Our solutions provide hardware-based security foundations for critical systems requiring the highest levels of protection against tampering and reverse engineering.',
      icon: Shield,
      image: 'https://images.unsplash.com/photo-1634452021892-eaabbdc4a0bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1650530415027-dc9199f473ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        <div className="container relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-16 flex items-center justify-center">
                <Cpu className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Research & <span className="text-royal-gold">Development</span>
                </h1>
              </div>
            </div>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Cutting-edge research and development in embedded systems, IoT solutions, and specialized spy devices. Our R&D capabilities combine advanced electronics, software integration, and custom hardware design for intelligence, defense, and security applications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <div className="space-y-20">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-16 flex items-center justify-center">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {feature.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">{feature.description}</p>
                </div>
                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img src={feature.image} alt={feature.title} className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-12 flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchDevelopmentPage;